import { useEffect, useMemo, useState } from "react";
import {
  Row, Col, Card, Button, Select, InputNumber, Input, DatePicker,
  Space, Form, message, Alert, Badge, Progress, Typography, Empty, Tag
} from "antd";
import {
  PlusOutlined, DeleteOutlined, UserOutlined, EnvironmentOutlined,
  InboxOutlined, EditOutlined, ShoppingCartOutlined, IdcardOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getStockProductoPO as getStockProducto } from "../../../../services/financiero/ingresosPO/entregasPOService";

const { Option } = Select;
const { Text } = Typography;

const filterOption = (input, option) =>
  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase());

const createEmptyProductoRow = () => ({
  key: Date.now() + Math.random(),
  oid_producto: null,
  cantidad: null
});

const cardHeaderStyle = {
  background: "#042956",
  color: "#fff",
  borderRadius: "12px 12px 0 0",
  minHeight: 46
};

const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  marginBottom: 24,
  overflow: "hidden"
};

const CardTitle = ({ icon, children }) => (
  <Space size={10}>
    {icon}
    <span style={{ fontWeight: 600, fontSize: 14 }}>{children}</span>
  </Space>
);

export default function EntregaActaForm({
  participantes = [],
  organizaciones = [],
  productos = [],
  municipios = [],
  departamentos = [],
  proyectos = [],
  bodegas = [],
  responsables = [],
  onChange,
  onValidationChange
}) {

  const [header, setHeader] = useState({
    tipo_entrega: 1,
    oid_participante: null,
    oid_proyecto: null,
    oid_departamento: null,
    oid_municipio: null,
    oid_bodega: null,
    tipo_acta: "Parcial",
    fecha_entrega: dayjs(),
    lugar_entrega: "",
    carpeta: "",
    nombre_archivo: "",
    oid_usuario_entrega: null,
    oid_usuario_supervisor: null,
    representante_legal_nombre: "",
    representante_legal_cedula: "",
    camara_comercio_codigo: "",
    camara_comercio_fecha: null,
    telefono_contacto_entrega: ""
  });

  const [productoRows, setProductoRows] = useState([createEmptyProductoRow()]);
  const [stockMap, setStockMap] = useState({});
  const [errors, setErrors] = useState({});

  const organizacionSeleccionada = useMemo(
    () => organizaciones.find((o) => Number(o.oid) === Number(header.oid_participante)),
    [organizaciones, header.oid_participante]
  );

  const municipiosFiltrados = useMemo(() => {
    if (!header.oid_departamento) return [];
    return municipios.filter(
      (m) => Number(m.oid_depto) === Number(header.oid_departamento)
    );
  }, [municipios, header.oid_departamento]);

  // La bodega es una ubicación física: si ya se eligió municipio, se prioriza
  // mostrar solo las bodegas de ese municipio; si ninguna coincide (o aún no
  // hay municipio elegido) se muestran todas para no dejar al usuario sin opciones.
  const bodegasFiltradas = useMemo(() => {
    if (!header.oid_municipio) return bodegas;
    const enElMunicipio = bodegas.filter((b) => Number(b.oid_municipio) === Number(header.oid_municipio));
    return enElMunicipio.length ? enElMunicipio : bodegas;
  }, [bodegas, header.oid_municipio]);

  const productosFiltrados = useMemo(() => {
    return productos.filter((p) => {
      const conStock = Number(p.stock_disponible) > 0;
      const delProyecto = !header.oid_proyecto || Number(p.oid_proyecto) === Number(header.oid_proyecto);
      return conStock && delProyecto;
    });
  }, [productos, header.oid_proyecto]);

  const getReservedByProduct = (productoId, excludeRowKey = null) => {
    if (!productoId) return 0;
    return productoRows.reduce((acc, row) => {
      if (row.key !== excludeRowKey && Number(row.oid_producto) === Number(productoId)) {
        return acc + Number(row.cantidad || 0);
      }
      return acc;
    }, 0);
  };

  const emitChanges = (updatedHeader, updatedRows, updatedErrors) => {
    const payload = updatedRows
      .filter((r) => r.oid_producto && r.cantidad)
      .map((r) => ({
        oid_participante: updatedHeader.oid_participante,
        tipo_entrega: updatedHeader.tipo_entrega,
        oid_proyecto: updatedHeader.oid_proyecto,
        oid_municipio: updatedHeader.oid_municipio,
        oid_bodega: updatedHeader.oid_bodega,
        tipo_acta: updatedHeader.tipo_acta,
        fecha_entrega: updatedHeader.fecha_entrega,
        lugar_entrega: updatedHeader.lugar_entrega,
        carpeta: updatedHeader.carpeta,
        nombre_archivo: updatedHeader.nombre_archivo,
        oid_usuario_entrega: updatedHeader.oid_usuario_entrega,
        oid_usuario_supervisor: updatedHeader.oid_usuario_supervisor,
        responsable: responsables.find((r) => r.login === updatedHeader.oid_usuario_entrega)?.name || null,
        representante_legal_nombre: updatedHeader.tipo_entrega === 2 ? updatedHeader.representante_legal_nombre : null,
        representante_legal_cedula: updatedHeader.tipo_entrega === 2 ? updatedHeader.representante_legal_cedula : null,
        camara_comercio_codigo: updatedHeader.tipo_entrega === 2 ? updatedHeader.camara_comercio_codigo : null,
        camara_comercio_fecha: updatedHeader.tipo_entrega === 2 && updatedHeader.camara_comercio_fecha
          ? updatedHeader.camara_comercio_fecha.format("YYYY-MM-DD")
          : null,
        telefono_contacto_entrega: updatedHeader.tipo_entrega === 2 ? updatedHeader.telefono_contacto_entrega : null,
        oid_producto: r.oid_producto,
        cantidad: r.cantidad
      }));

    onChange?.(payload);

    const headerOk =
      !!updatedHeader.oid_participante &&
      !!updatedHeader.oid_proyecto &&
      !!updatedHeader.oid_departamento &&
      !!updatedHeader.oid_municipio &&
      !!updatedHeader.oid_bodega &&
      !!updatedHeader.fecha_entrega &&
      !!updatedHeader.oid_usuario_entrega &&
      !!updatedHeader.oid_usuario_supervisor;

    const rowsOk =
      updatedRows.length > 0 &&
      updatedRows.every((row) => Object.keys(updatedErrors[row.key] || {}).length === 0) &&
      payload.length > 0;

    onValidationChange?.({ isValid: headerOk && rowsOk, rows: payload });
  };

  const validateRows = (rows = productoRows) => {
    const newErrors = {};
    rows.forEach((row) => {
      const rowErr = {};
      if (!row.oid_producto) rowErr.oid_producto = "Seleccione el producto";
      if (!row.cantidad || Number(row.cantidad) <= 0) rowErr.cantidad = "Cantidad inválida";
      if (row.oid_producto && row.cantidad) {
        const stockBd = Number(stockMap[row.key] || 0);
        const reserved = getReservedByProduct(row.oid_producto, row.key);
        const available = Math.max(stockBd - reserved, 0);
        if (Number(row.cantidad) > available) {
          rowErr.cantidad = `Stock insuficiente. Disponible: ${available}`;
        }
      }
      newErrors[row.key] = rowErr;
    });
    setErrors(newErrors);
    emitChanges(header, rows, newErrors);
  };

  useEffect(() => {
    validateRows(productoRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productoRows, stockMap, header]);

  // Si la bodega elegida deja de pertenecer a la lista filtrada (cambió el
  // municipio), se limpia para que el usuario no se quede con una selección
  // que ya no aplica sin darse cuenta.
  useEffect(() => {
    if (header.oid_bodega && !bodegasFiltradas.some((b) => Number(b.oid) === Number(header.oid_bodega))) {
      setHeader((prev) => ({ ...prev, oid_bodega: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodegasFiltradas]);

  const updateHeader = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleTipoEntregaChange = (value) => {
    setHeader((prev) => ({
      ...prev,
      tipo_entrega: value,
      oid_participante: null,
      oid_proyecto: null
    }));
  };

  const handleBeneficiarioChange = (value) => {
    if (header.tipo_entrega === 1) {
      const participante = participantes.find((p) => Number(p.oid) === Number(value));
      const municipio = municipios.find((m) => Number(m.oid) === Number(participante?.oid_municipio));
      setHeader((prev) => ({
        ...prev,
        oid_participante: value,
        oid_departamento: municipio ? municipio.oid_depto : prev.oid_departamento,
        oid_municipio: participante?.oid_municipio || prev.oid_municipio
      }));
    } else {
      const organizacion = organizaciones.find((o) => Number(o.oid) === Number(value));
      setHeader((prev) => ({
        ...prev,
        oid_participante: value,
        oid_proyecto: organizacion?.oid_proyecto || prev.oid_proyecto
      }));
    }
  };

  const handleDepartamentoChange = (value) => {
    setHeader((prev) => ({ ...prev, oid_departamento: value, oid_municipio: null }));
  };

  const handleProductoRowChange = async (rowKey, productoId) => {
    const updated = productoRows.map((row) =>
      row.key === rowKey ? { ...row, oid_producto: productoId, cantidad: 1 } : row
    );
    setProductoRows(updated);
    try {
      const stock = await getStockProducto(productoId);
      setStockMap((prev) => ({ ...prev, [rowKey]: Number(stock.stock_disponible || 0) }));
    } catch {
      message.error("No se pudo consultar el stock del producto");
    }
  };

  const handleCantidadRowChange = (rowKey, cantidad) => {
    const row = productoRows.find((r) => r.key === rowKey);
    if (!row?.oid_producto) return;
    const stockBd = Number(stockMap[rowKey] || 0);
    const reserved = getReservedByProduct(row.oid_producto, rowKey);
    const available = Math.max(stockBd - reserved, 0);
    if (cantidad > available) {
      message.warning(`Solo quedan ${available} unidades disponibles para este producto`);
      cantidad = available;
    }
    setProductoRows(productoRows.map((r) => (r.key === rowKey ? { ...r, cantidad } : r)));
  };

  const addProductoRow = () => setProductoRows([...productoRows, createEmptyProductoRow()]);

  const removeProductoRow = (key) => {
    if (productoRows.length === 1) return;
    setProductoRows(productoRows.filter((r) => r.key !== key));
    setStockMap((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const totalLineas = productoRows.filter((r) => r.oid_producto && r.cantidad).length;

  return (
    <div style={{ paddingBottom: 8 }}>
      <Alert
        type="info"
        showIcon
        style={{ borderRadius: 10, marginBottom: 24 }}
        message="Registra una acta de entrega"
        description="Selecciona un beneficiario, la bodega de origen y una o varias líneas de producto con stock disponible."
      />

      <Form layout="vertical" component={false}>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<UserOutlined />}>Beneficiario</CardTitle>}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={8}>
              <Form.Item label="Tipo de entrega" required>
                <Select size="large" value={header.tipo_entrega} onChange={handleTipoEntregaChange}>
                  <Option value={1}>Participante</Option>
                  <Option value={2}>Organización</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item label="Beneficiario" required>
                <Select
                  size="large"
                  showSearch
                  allowClear
                  placeholder={header.tipo_entrega === 1 ? "Buscar participante por nombre" : "Buscar organización por nombre o NIT"}
                  value={header.oid_participante}
                  onChange={handleBeneficiarioChange}
                  filterOption={filterOption}
                >
                  {header.tipo_entrega === 1
                    ? participantes.map((p) => (
                      <Option key={p.oid} value={p.oid}>
                        {p.nombre_participante} - {p.numero_identificacion}
                      </Option>
                    ))
                    : organizaciones.map((o) => (
                      <Option key={o.oid} value={o.oid}>
                        {o.organizacion} - {o.Nit}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {header.tipo_entrega === 2 && (
            <>
              <Alert
                type="warning"
                showIcon
                icon={<IdcardOutlined />}
                message="Datos para el acta de organización"
                description="Se capturan por cada acta (no se guardan en el catálogo de organizaciones) y salen del certificado de cámara de comercio vigente."
                style={{ marginTop: 8, marginBottom: 20, borderRadius: 10 }}
              />
              <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item label="Representante legal">
                    <Input
                      size="large"
                      value={header.representante_legal_nombre}
                      onChange={(e) => updateHeader("representante_legal_nombre", e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Cédula representante legal">
                    <Input
                      size="large"
                      value={header.representante_legal_cedula}
                      onChange={(e) => updateHeader("representante_legal_cedula", e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Código cámara comercio">
                    <Input
                      size="large"
                      value={header.camara_comercio_codigo}
                      onChange={(e) => updateHeader("camara_comercio_codigo", e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Fecha certificado">
                    <DatePicker
                      size="large"
                      style={{ width: "100%" }}
                      value={header.camara_comercio_fecha}
                      onChange={(v) => updateHeader("camara_comercio_fecha", v)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Teléfono de contacto">
                    <Input
                      size="large"
                      value={header.telefono_contacto_entrega}
                      onChange={(e) => updateHeader("telefono_contacto_entrega", e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<EnvironmentOutlined />}>Proyecto y ubicación</CardTitle>}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={8}>
              <Form.Item label="Proyecto" required>
                <Select
                  size="large"
                  showSearch
                  placeholder="Seleccione el proyecto"
                  value={header.oid_proyecto}
                  onChange={(v) => updateHeader("oid_proyecto", v)}
                  filterOption={filterOption}
                  disabled={header.tipo_entrega === 2 && !!organizacionSeleccionada}
                >
                  {proyectos.map((p) => (
                    <Option key={p.oid} value={p.oid}>
                      {p.codigo} - {p.proyecto}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Departamento" required>
                <Select
                  size="large"
                  showSearch
                  placeholder="Seleccione el departamento"
                  value={header.oid_departamento}
                  onChange={handleDepartamentoChange}
                  filterOption={filterOption}
                >
                  {departamentos.map((d) => (
                    <Option key={d.oid} value={d.oid}>{d.departamento}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Municipio" required>
                <Select
                  size="large"
                  showSearch
                  disabled={!header.oid_departamento}
                  value={header.oid_municipio}
                  onChange={(v) => updateHeader("oid_municipio", v)}
                  filterOption={filterOption}
                  placeholder={header.oid_departamento ? "Seleccione municipio" : "Primero seleccione un departamento"}
                >
                  {municipiosFiltrados.map((m) => (
                    <Option key={m.oid} value={m.oid}>{m.municipio}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<InboxOutlined />}>Entrega</CardTitle>}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Bodega de origen"
                required
                tooltip={header.oid_municipio ? "Filtrada por el municipio seleccionado" : undefined}
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="Seleccione la bodega"
                  value={header.oid_bodega}
                  onChange={(v) => updateHeader("oid_bodega", v)}
                  filterOption={filterOption}
                >
                  {bodegasFiltradas.map((b) => (
                    <Option key={b.oid} value={b.oid}>
                      {b.sigla} - {b.organizacion_nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Tipo de acta" required>
                <Select size="large" value={header.tipo_acta} onChange={(v) => updateHeader("tipo_acta", v)}>
                  <Option value="Parcial">Parcial</Option>
                  <Option value="Final">Final</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Fecha de entrega" required>
                <DatePicker
                  size="large"
                  style={{ width: "100%" }}
                  value={header.fecha_entrega}
                  onChange={(v) => updateHeader("fecha_entrega", v)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Lugar de entrega">
                <Input
                  size="large"
                  placeholder="Ej: Vereda Pueblo Nuevo"
                  value={header.lugar_entrega}
                  onChange={(e) => updateHeader("lugar_entrega", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Carpeta">
                <Input
                  size="large"
                  placeholder="Opcional"
                  value={header.carpeta}
                  onChange={(e) => updateHeader("carpeta", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Nombre archivo">
                <Input
                  size="large"
                  placeholder="Opcional"
                  value={header.nombre_archivo}
                  onChange={(e) => updateHeader("nombre_archivo", e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={cardStyle}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<EditOutlined />}>Firmas</CardTitle>}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Form.Item label="Quien entrega" required>
                <Select
                  size="large"
                  showSearch
                  placeholder="Seleccione el responsable de la entrega"
                  value={header.oid_usuario_entrega}
                  onChange={(v) => updateHeader("oid_usuario_entrega", v)}
                  filterOption={filterOption}
                >
                  {responsables.map((r) => (
                    <Option key={r.login} value={r.login}>{r.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Supervisor convenio" required>
                <Select
                  size="large"
                  showSearch
                  placeholder="Seleccione el supervisor del convenio"
                  value={header.oid_usuario_supervisor}
                  onChange={(v) => updateHeader("oid_usuario_supervisor", v)}
                  filterOption={filterOption}
                >
                  {responsables.map((r) => (
                    <Option key={r.login} value={r.login}>{r.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          style={{ ...cardStyle, marginBottom: 0 }}
          styles={{ header: cardHeaderStyle, body: { padding: 24 } }}
          title={<CardTitle icon={<ShoppingCartOutlined />}>Productos entregados</CardTitle>}
          extra={totalLineas > 0 && (
            <Tag color="blue" style={{ marginRight: 0 }}>
              {totalLineas} línea{totalLineas > 1 ? "s" : ""}
            </Tag>
          )}
        >
          {!header.oid_proyecto && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 20, borderRadius: 10 }}
              message="Selecciona un proyecto para ver los productos con stock disponible."
            />
          )}

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addProductoRow}
            block
            style={{ marginBottom: 20, height: 44, borderRadius: 8, fontWeight: 500 }}
          >
            Agregar línea de producto
          </Button>

          <Space direction="vertical" style={{ width: "100%" }} size={16}>
            {productoRows.map((row, index) => {
              const rowErr = errors[row.key] || {};
              const stockBd = Number(stockMap[row.key] || 0);
              const reserved = getReservedByProduct(row.oid_producto, row.key);
              const available = Math.max(stockBd - reserved, 0);
              const porcentaje = stockBd ? Math.round((reserved / stockBd) * 100) : 0;
              return (
                <Card
                  key={row.key}
                  size="small"
                  style={{ borderRadius: 10, border: "1px solid #e8ebf0" }}
                  styles={{
                    header: { ...cardHeaderStyle, borderRadius: "10px 10px 0 0", minHeight: 40, padding: "0 16px" },
                    body: { padding: 20 }
                  }}
                  title={<span style={{ fontSize: 13, fontWeight: 500 }}>Línea {index + 1}</span>}
                  extra={
                    productoRows.length > 1 && (
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined style={{ color: "#fff" }} />}
                        onClick={() => removeProductoRow(row.key)}
                      />
                    )
                  }
                >
                  <Row gutter={[24, 16]} align="top">
                    <Col xs={24} md={14}>
                      <Form.Item
                        label="Producto"
                        required
                        validateStatus={rowErr.oid_producto ? "error" : ""}
                        help={rowErr.oid_producto}
                        style={{ marginBottom: 10 }}
                      >
                        <Select
                          size="large"
                          showSearch
                          placeholder="Seleccione producto con stock"
                          value={row.oid_producto}
                          onChange={(v) => handleProductoRowChange(row.key, v)}
                          filterOption={filterOption}
                        >
                          {productosFiltrados.map((p) => (
                            <Option key={p.oid} value={p.oid}>
                              {p.nombre_corto || p.nombre_producto} ({p.stock_disponible} disp.)
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      {row.oid_producto && (
                        <Progress
                          percent={porcentaje}
                          size="small"
                          status={available === 0 ? "exception" : "active"}
                          format={() => `${available} disponibles`}
                        />
                      )}
                    </Col>
                    <Col xs={16} md={6}>
                      <Form.Item
                        label="Cantidad"
                        required
                        validateStatus={rowErr.cantidad ? "error" : ""}
                        help={rowErr.cantidad}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          size="large"
                          min={1}
                          max={available || 1}
                          style={{ width: "100%" }}
                          value={row.cantidad}
                          disabled={!row.oid_producto || available <= 0}
                          onChange={(v) => handleCantidadRowChange(row.key, v)}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={8} md={4} style={{ display: "flex", alignItems: "center", paddingTop: 30 }}>
                      {row.oid_producto && (
                        <Badge status={available > 0 ? "success" : "error"} text={`${available} disp.`} />
                      )}
                    </Col>
                  </Row>
                </Card>
              );
            })}
          </Space>

          {!productosFiltrados.length && header.oid_proyecto && (
            <Empty
              style={{ marginTop: 20 }}
              description="No hay productos con stock disponible para el proyecto seleccionado."
            />
          )}
        </Card>
      </Form>
    </div>
  );
}
